import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, Input, InputNumber, Modal, Select, Spin, notification } from "antd";
import { NotificationManager } from "react-notifications";
import PurchaseOrderServices from "../../services/purchaseOrder.service";
import CatalogJewelryDataService from "../../services/catalog-jewelry.service";
import PoLineItemsTable from "./shared/PoLineItemsTable";
import OrderContactPicker from "./shared/OrderContactPicker";
import "../../assets/css/po2-design.css";

const { Option } = Select;

// Task 1901 (Feature 1861 / F3): the vendor builds a proposed order for a retailer - the
// opposite direction from today's retailer-writes-a-worksheet flow. Talks to the new F3
// endpoints (CreateVendorWorksheet/UpsertOrderLine/RemoveOrderLine) plus F2's
// SetOrderTerms/Transition, all vendor-only + DRAFT-only on the backend.
//
// Epic 1857 UI-redesign (Phase 2): restyled to match the approved prototype's vNew()
// layout (PO-Prototype-v2.html) - numbered-section panels with completion dots, sticky
// totals rail, close-order action list. No backend/handler logic changed from the
// original F3 build, only presentation + two real UX fixes named as hard requirements by
// the spec: (1) close-order actions (esp. Cash and Carry) restate the buyer and exact
// amount in a confirm modal before committing, and (2) catalog search is inline type-ahead
// instead of a separate modal, matching the prototype exactly.
const TERMS_OPTIONS = [
  { value: "CASH", label: "Cash and Carry" },
  { value: "NET7", label: "Net 7" },
  { value: "NET15", label: "Net 15" },
  { value: "NET30", label: "Net 30" },
  { value: "NET60", label: "Net 60" },
  { value: "NET90", label: "Net 90" },
  { value: "CUSTOM", label: "Custom" },
];

const CLOSE_ACTIONS = [
  { event: "CONFIRM_BEHALF", label: "Confirm on Their Behalf", note: "Commits without their login. Records who confirmed. They may still dispute.", needsName: true },
  { event: "INVOICE", label: "Mark as Invoice", note: "Finalizes without their approval. They are notified and may dispute.", confirm: true },
  { event: "CASH_CARRY", label: "Cash and Carry", note: "Terminal. They cannot edit, reject or dispute. A change of mind becomes a return.", confirm: true, terminal: true },
];

const DoneDot = ({ done }) => (
  <span style={{ color: done ? "var(--po2-ok)" : "#c3cbe4", fontWeight: 700, marginInlineEnd: 8 }}>●</span>
);

const VendorOrderBuilder = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const navigate = useNavigate();
  const loginDetials = useSelector((state) => state.loginReducer);
  const dealerId = loginDetials?.loginDetials?.responseData?.dealerId;

  const [poId, setPoId] = useState(null);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  const [orderContact, setOrderContact] = useState(null);

  // Task 1917 (Feature 1864 / F6): exactly one active show -> auto-selected (read-only
  // note). Zero or multiple -> a plain dropdown, "no show" always an option.
  const [activeShows, setActiveShows] = useState([]);
  const [selectedShowId, setSelectedShowId] = useState(null);
  const [showsChecked, setShowsChecked] = useState(false);

  useEffect(() => {
    PurchaseOrderServices.GetActiveShows()
      .then((res) => {
        const shows = res?.data?.status ? res.data.responseData || [] : [];
        setActiveShows(shows);
        if (shows.length === 1) {
          setSelectedShowId(shows[0].showId);
        }
        setShowsChecked(true);
      })
      .catch(() => setShowsChecked(true));
  }, []);

  const [adHocModalVisible, setAdHocModalVisible] = useState(false);
  const [adHoc, setAdHoc] = useState({
    styleNumber: "",
    description: "",
    note: "",
    price: "",
    quantity: 1,
  });

  // Inline type-ahead catalog search (matches the prototype's onSugg/suggKey) - replaces
  // the earlier modal-based search, same underlying ViewProduct call, debounced so it
  // doesn't fire a request on every keystroke.
  const [catalogQuery, setCatalogQuery] = useState("");
  const [catalogSuggestions, setCatalogSuggestions] = useState([]);
  const [catalogSearching, setCatalogSearching] = useState(false);
  const [suggestionIndex, setSuggestionIndex] = useState(0);
  const searchDebounceRef = useRef(null);

  const [terms, setTerms] = useState({
    termsCode: "NET30",
    orderDiscountPct: 0,
    earlyPayPercent: 0,
    earlyPayDays: 0,
    referenceNumber: "",
  });

  const [confirmNameModal, setConfirmNameModal] = useState(false);
  const [confirmedByName, setConfirmedByName] = useState("");

  // Close-order confirm modal restating buyer + exact amount (spec's own named hard
  // requirement - "gated by a modal restating buyer and exact amount", not just a plain
  // confirm() dialog with a title).
  const [closeConfirm, setCloseConfirm] = useState(null); // { event, label, note, terminal }

  const refreshOrder = (id) => {
    const targetId = id || poId;
    if (!targetId) return;
    PurchaseOrderServices.GetOrderDetail(targetId).then((res) => {
      if (res?.data?.status) {
        setOrder(res.data.responseData);
      }
    });
  };

  const handleStartOrder = () => {
    if (!orderContact) {
      NotificationManager.error(t("Select a retailer or recipient first."));
      return;
    }
    setLoading(true);
    PurchaseOrderServices.CreateVendorWorksheet({
      retailerDealerId: orderContact.retailerDealerId,
      orderContactId: orderContact.orderContactId,
      showId: selectedShowId,
      referenceNumber: terms.referenceNumber,
      termsCode: terms.termsCode,
      orderDiscountPct: terms.orderDiscountPct,
      earlyPayPercent: terms.earlyPayPercent,
      earlyPayDays: terms.earlyPayDays,
    })
      .then((res) => {
        setLoading(false);
        if (res?.data?.status) {
          const newPoId = res.data.responseData.poId;
          setPoId(newPoId);
          refreshOrder(newPoId);
        } else {
          NotificationManager.error(t("Could not start the order."));
        }
      })
      .catch(() => {
        setLoading(false);
        NotificationManager.error(t("Could not start the order."));
      });
  };

  const upsertLine = (fields, lineId) => {
    if (!poId) {
      NotificationManager.error(t("Start the order first."));
      return;
    }
    setLoading(true);
    PurchaseOrderServices.UpsertOrderLine({ poId, lineId: lineId || null, ...fields })
      .then((res) => {
        setLoading(false);
        if (res?.data?.status) {
          refreshOrder();
        } else {
          NotificationManager.error(t("Could not save the line."));
        }
      })
      .catch(() => {
        setLoading(false);
        NotificationManager.error(t("Could not save the line."));
      });
  };

  const runCatalogSearch = (query) => {
    if (!query.trim()) {
      setCatalogSuggestions([]);
      setCatalogSearching(false);
      return;
    }
    setCatalogSearching(true);
    CatalogJewelryDataService.ViewProduct(
      {
        dealerID: dealerId?.toString(),
        orderby: "GFInventoryID",
        order: "ASC",
        category: "",
        vendor: "",
        materialType: "",
        gender: "",
        collection: "",
        collectionIDs: "",
        materialColor: "",
        minRange: "",
        maxRange: "",
        styleNumber: query,
        dealerType: "MyProduct",
        noOfRows: "8",
        offset: "0",
        strSelectedInvID: "",
        hasImage: "",
        viewBy: "2",
      },
      undefined
    )
      .then((res) => {
        setCatalogSearching(false);
        setCatalogSuggestions(res?.data?.responseData || []);
      })
      .catch(() => setCatalogSearching(false));
  };

  const handleCatalogQueryChange = (value) => {
    setCatalogQuery(value);
    setSuggestionIndex(0);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => runCatalogSearch(value), 350);
  };

  const addCatalogLine = (product) => {
    upsertLine({
      jewelryInventoryId: product.gfInventoryID,
      quantity: 1,
      discountType: "NONE",
      discountValue: 0,
    });
    setCatalogQuery("");
    setCatalogSuggestions([]);
  };

  const handleCatalogSearchKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      setSuggestionIndex((i) => Math.min(i + 1, catalogSuggestions.length - 1));
      e.preventDefault();
    } else if (e.key === "ArrowUp") {
      setSuggestionIndex((i) => Math.max(i - 1, 0));
      e.preventDefault();
    } else if (e.key === "Enter" && catalogSuggestions[suggestionIndex]) {
      addCatalogLine(catalogSuggestions[suggestionIndex]);
      e.preventDefault();
    }
  };

  const saveAdHoc = () => {
    if (!adHoc.price) {
      NotificationManager.error(t("A unit price is required for an ad hoc line."));
      return;
    }
    upsertLine({
      adHocStyleNumber: adHoc.styleNumber,
      adHocDescription: adHoc.description,
      adHocNote: adHoc.note,
      adHocListUnitPrice: Number(adHoc.price),
      quantity: Number(adHoc.quantity) || 1,
      discountType: "NONE",
      discountValue: 0,
    });
    setAdHocModalVisible(false);
    setAdHoc({ styleNumber: "", description: "", note: "", price: "", quantity: 1 });
  };

  const lineFields = (line) => ({
    jewelryInventoryId: line.jewelryInventoryId,
    adHocStyleNumber: line.adHocStyleNumber,
    adHocDescription: line.adHocDescription,
    adHocNote: line.adHocNote,
    adHocListUnitPrice: line.isAdHoc ? line.listUnitPrice : undefined,
    quantity: line.quantity,
    discountType: line.discountType,
    discountValue: line.discountValue,
  });

  const handleQtyChange = (lineId, quantity) => {
    const line = order?.lines?.find((l) => l.lineId === lineId);
    if (!line) return;
    upsertLine({ ...lineFields(line), quantity }, lineId);
  };

  const handleDiscountChange = (lineId, discountType, discountValue) => {
    const line = order?.lines?.find((l) => l.lineId === lineId);
    if (!line) return;
    upsertLine({ ...lineFields(line), discountType, discountValue }, lineId);
  };

  // Matches the prototype's own toast+Undo on line removal. The vendor always owns the
  // lines they write, so re-creating via UpsertOrderLine on Undo is safe here - it is NOT
  // safe to offer the same on the retailer's review-mode remove (OrderDetail.js), since
  // PoLineBuilder attributes an ad hoc line's VendorId to whoever calls the API
  // (PoLineBuilder.cs:105) - a retailer "undoing" a remove would create the line under
  // their own DealerId instead of the real vendor's.
  const handleRemoveLine = (lineId) => {
    const removedLine = order?.lines?.find((l) => l.lineId === lineId);
    setLoading(true);
    PurchaseOrderServices.RemoveOrderLine({ poId, lineId })
      .then((res) => {
        setLoading(false);
        if (res?.data?.status) {
          refreshOrder();
          if (removedLine) {
            const key = `undo-line-${lineId}`;
            notification.open({
              key,
              message: t("Removed"),
              description: removedLine.isAdHoc ? removedLine.adHocStyleNumber || t("Ad hoc line") : removedLine.styleNumber || removedLine.jewelryInventoryId,
              duration: 6,
              btn: (
                <Button
                  size="small"
                  type="primary"
                  className="primary-btn"
                  onClick={() => {
                    notification.close(key);
                    upsertLine(lineFields(removedLine));
                  }}
                >
                  {t("Undo")}
                </Button>
              ),
            });
          }
        }
      })
      .catch(() => setLoading(false));
  };

  const saveTerms = () => {
    if (!poId) return;
    setLoading(true);
    PurchaseOrderServices.SetOrderTerms({
      poId,
      termsCode: terms.termsCode,
      termsCustomText: "",
      earlyPayPercent: terms.earlyPayPercent,
      earlyPayDays: terms.earlyPayDays,
      orderDiscountPct: terms.orderDiscountPct,
      referenceNumber: terms.referenceNumber,
    })
      .then((res) => {
        setLoading(false);
        if (res?.data?.status) {
          NotificationManager.success(t("Terms saved."));
          refreshOrder();
        }
      })
      .catch(() => setLoading(false));
  };

  const doTransition = (event, extra) => {
    if (!poId) return;
    setLoading(true);
    PurchaseOrderServices.Transition({ poId, event, ...extra })
      .then((res) => {
        setLoading(false);
        if (res?.data?.status) {
          NotificationManager.success(t("Order updated."));
          navigate("/myPurchaseOrder");
        } else {
          NotificationManager.error(t("That action isn't allowed right now."));
        }
      })
      .catch(() => {
        setLoading(false);
        NotificationManager.error(t("That action isn't allowed right now."));
      });
  };

  const handleSend = () => doTransition("SEND");

  // Task 1904/1905 (Feature 1863 / F5, Epic 1857 UI-redesign Phase 8): always generates a
  // fresh PDF rather than reusing a previously-stored one, so the preview matches the
  // order's current lines/discounts/terms - not a stale snapshot from before the vendor's
  // latest edit. The order is DRAFT for this whole screen (backend enforces it), so the
  // document type is always PROPOSAL here.
  const handlePreviewDocument = () => {
    if (!poId) return;
    setLoading(true);
    PurchaseOrderServices.GenerateOrderDocument(poId, "PROPOSAL")
      .then((res) => {
        setLoading(false);
        if (res?.data?.status && res.data.responseData?.documentUrl) {
          window.open(res.data.responseData.documentUrl, "_blank", "noopener");
        } else {
          NotificationManager.error(t("Could not generate the document."));
        }
      })
      .catch(() => {
        setLoading(false);
        NotificationManager.error(t("Could not generate the document."));
      });
  };

  const handleCloseAction = (action) => {
    if (action.needsName) {
      setConfirmNameModal(true);
      return;
    }
    // Every other close action is confirm:true in CLOSE_ACTIONS - restate buyer + amount.
    setCloseConfirm(action);
  };

  const hasWho = !!orderContact;
  const hasLines = (order?.lines?.length || 0) > 0;
  const readyToClose = hasLines;

  return (
    <div className="po2-scope" dir={isRtl ? "rtl" : "ltr"}>
      <div className="po2-phead">
        <div>
          <h1>{t("Create Order")}</h1>
          <div className="po2-desc">{t("Vendor-initiated worksheet")}</div>
        </div>
      </div>

      <Spin spinning={loading}>
        {!poId ? (
          <div className="po2-panel">
            <div className="po2-ph">
              <DoneDot done={hasWho} />
              <h3>{t("Who is buying")}</h3>
            </div>
            <OrderContactPicker dealerId={dealerId} value={orderContact} onChange={setOrderContact} />

            {showsChecked && activeShows.length === 1 && (
              <div className="po2-field" style={{ marginTop: 12 }}>
                <label>{t("Show")}</label>
                <div className="po2-pick po2-on" style={{ cursor: "default" }}>
                  <div>
                    <b>{activeShows[0].showName}</b>
                    <span>{t("auto-detected")}</span>
                  </div>
                </div>
              </div>
            )}
            {showsChecked && activeShows.length !== 1 && (
              <div className="po2-field" style={{ marginTop: 12 }}>
                <label>{t("Show")}</label>
                <Select
                  style={{ width: "100%" }}
                  placeholder={t("No show (optional)")}
                  allowClear
                  value={selectedShowId}
                  onChange={(val) => setSelectedShowId(val ?? null)}
                >
                  {activeShows.map((s) => (
                    <Option key={s.showId} value={s.showId}>
                      {s.showName}
                    </Option>
                  ))}
                </Select>
              </div>
            )}

            <button className="po2-btn" style={{ marginTop: 16 }} onClick={handleStartOrder}>
              {t("Start Order")}
            </button>
          </div>
        ) : (
          <div className="po2-grid po2-rail-grid">
            <div>
              <div className="po2-panel">
                <div className="po2-ph">
                  <DoneDot done={true} />
                  <h3>{t("Who is buying")}</h3>
                </div>
                <div className="po2-pick po2-on" style={{ cursor: "default" }}>
                  <div>
                    <b>{orderContact?.label}</b>
                  </div>
                </div>
              </div>

              <div className="po2-panel po2-flush">
                <div className="po2-ph">
                  <DoneDot done={hasLines} />
                  <h3>{t("Line items")}</h3>
                  <span className="po2-desc">
                    {order?.lines?.length || 0} {t("lines")}
                  </span>
                  <div className="po2-right">
                    <button className="po2-btn po2-sm po2-neutral" onClick={() => setAdHocModalVisible(true)}>
                      {t("+ Ad hoc")}
                    </button>
                  </div>
                </div>
                <div className="po2-searchadd" style={{ position: "relative" }}>
                  <input
                    className="po2-in"
                    placeholder={t("Type a style number or description, then Enter to add...")}
                    value={catalogQuery}
                    onChange={(e) => handleCatalogQueryChange(e.target.value)}
                    onKeyDown={handleCatalogSearchKeyDown}
                  />
                  <span className="po2-hint" style={{ margin: 0, whiteSpace: "nowrap" }}>
                    {t("Only your styles")}
                  </span>
                </div>
                {catalogSuggestions.length > 0 && (
                  <div className="po2-sugg">
                    {catalogSuggestions.map((s, i) => (
                      <div
                        key={s.gfInventoryID}
                        className={i === suggestionIndex ? "po2-sel" : ""}
                        onClick={() => addCatalogLine(s)}
                      >
                        <b>{s.styleNumber}</b>
                        <span style={{ color: "var(--po2-muted)" }}>{s.productName}</span>
                        <span style={{ marginInlineStart: "auto", fontFamily: "var(--po2-mono)", color: "var(--po2-muted)" }}>
                          ${s.wholesaleprice}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {catalogSearching && (
                  <div className="po2-hint" style={{ padding: "0 16px 10px" }}>
                    {t("Searching...")}
                  </div>
                )}

                {hasLines ? (
                  <PoLineItemsTable
                    lines={order?.lines}
                    qtyEditable
                    discountEditable
                    showRemove
                    onQtyChange={handleQtyChange}
                    onDiscountChange={handleDiscountChange}
                    onRemove={handleRemoveLine}
                  />
                ) : (
                  <div className="po2-empty">
                    <div className="po2-ic">◇</div>
                    <p>{t("No lines yet. Search above, or add a piece that is in front of you but not in your catalog.")}</p>
                    <button className="po2-btn po2-neutral" onClick={() => setAdHocModalVisible(true)}>
                      {t("+ Ad hoc line")}
                    </button>
                  </div>
                )}
              </div>

              <div className="po2-panel">
                <div className="po2-ph">
                  <h3>{t("Discount, terms and reference")}</h3>
                </div>
                <div className="po2-filters" style={{ margin: 0, border: "none", padding: 0 }}>
                  <div className="po2-field">
                    <label>{t("Order Discount %")}</label>
                    <InputNumber
                      style={{ width: 120 }}
                      min={0}
                      max={100}
                      value={terms.orderDiscountPct}
                      onChange={(val) => setTerms({ ...terms, orderDiscountPct: val })}
                    />
                  </div>
                  <div className="po2-field">
                    <label>{t("Terms")}</label>
                    <Select
                      style={{ width: 160 }}
                      value={terms.termsCode}
                      onChange={(val) => setTerms({ ...terms, termsCode: val })}
                    >
                      {TERMS_OPTIONS.map((o) => (
                        <Option key={o.value} value={o.value}>
                          {t(o.label)}
                        </Option>
                      ))}
                    </Select>
                  </div>
                  <div className="po2-field">
                    <label>{t("Early Pay %")}</label>
                    <InputNumber
                      style={{ width: 110 }}
                      min={0}
                      max={100}
                      value={terms.earlyPayPercent}
                      onChange={(val) => setTerms({ ...terms, earlyPayPercent: val })}
                    />
                  </div>
                  <div className="po2-field">
                    <label>{t("Within days")}</label>
                    <InputNumber
                      style={{ width: 100 }}
                      min={0}
                      value={terms.earlyPayDays}
                      onChange={(val) => setTerms({ ...terms, earlyPayDays: val })}
                    />
                  </div>
                  <div className="po2-field">
                    <label>{t("Reference Number")}</label>
                    <Input
                      style={{ width: 210 }}
                      value={terms.referenceNumber}
                      onChange={(e) => setTerms({ ...terms, referenceNumber: e.target.value })}
                    />
                  </div>
                </div>
                <button className="po2-btn po2-sm" style={{ marginTop: 12 }} onClick={saveTerms}>
                  {t("Save Terms")}
                </button>
                <div className="po2-hint" style={{ marginTop: 10 }}>
                  {t("Terms are informational. The system does not track due dates, payment or aging.")}
                </div>
              </div>
            </div>

            <div className="po2-rail">
              <div className="po2-panel">
                <div className="po2-ph">
                  <h3>{t("Totals")}</h3>
                </div>
                <div className="po2-tot">
                  <span>{t("Subtotal")}</span>
                  <span className="po2-v">${order?.subtotalAmount?.toFixed(2) ?? "0.00"}</span>
                </div>
                <div className="po2-tot">
                  <span>{t("Discount")}</span>
                  <span className="po2-v po2-neg">-${order?.discountTotal?.toFixed(2) ?? "0.00"}</span>
                </div>
                <div className="po2-grandbox">
                  <div className="po2-lab">{t("Order Total")}</div>
                  <div className="po2-amt">${order?.orderTotal?.toFixed(2) ?? "0.00"}</div>
                </div>
                <div className="po2-hint" style={{ marginTop: 10 }}>
                  {t("No sales tax is stored or calculated. Every figure is the amount payable.")}
                </div>
              </div>

              <div className="po2-panel" style={{ marginBottom: 0 }}>
                <div className="po2-ph">
                  <h3>{t("Close the order")}</h3>
                </div>
                {readyToClose ? (
                  <>
                    <button className="po2-btn po2-full" onClick={handleSend}>
                      {t("Send to Retailer")}
                    </button>
                    <div className="po2-hint" style={{ marginTop: 6, marginBottom: 12 }}>
                      {t("Reversible until they act on it.")}
                    </div>
                    <div className="po2-divider">
                      <span>{t("Close without retailer approval")}</span>
                    </div>
                    {CLOSE_ACTIONS.map((a) => (
                      <button
                        key={a.event}
                        className={`po2-actionrow ${a.terminal ? "po2-term" : ""}`}
                        onClick={() => handleCloseAction(a)}
                      >
                        <b>
                          {t(a.label)} {a.terminal ? "🔒" : ""}
                        </b>
                        <span>{t(a.note)}</span>
                      </button>
                    ))}
                  </>
                ) : (
                  <div className="po2-hint">{t("Add at least one line to close this order.")}</div>
                )}
                <button className="po2-btn po2-neutral po2-sm po2-full" style={{ marginTop: 10 }} onClick={handlePreviewDocument} disabled={!hasLines}>
                  {t("Preview document")}
                </button>
              </div>
            </div>
          </div>
        )}
      </Spin>

      <Modal
        title={t("Add Ad Hoc Line")}
        visible={adHocModalVisible}
        onCancel={() => setAdHocModalVisible(false)}
        onOk={saveAdHoc}
      >
        <div className="po2-scope" dir={isRtl ? "rtl" : "ltr"} style={{ padding: 0, background: "transparent", border: "none" }}>
          <div className="po2-insight po2-warn">
            <b>{t("Never written back to your catalog")}</b>
            {t("This line exists only on this order. It is flagged as ad hoc on the document, in exports and in reporting.")}
          </div>
          <div className="po2-grid po2-g2">
            <div className="po2-field">
              <label>{t("Style Number")}</label>
              <input className="po2-in" value={adHoc.styleNumber} onChange={(e) => setAdHoc({ ...adHoc, styleNumber: e.target.value })} />
            </div>
            <div className="po2-field">
              <label>{t("Unit Price")} *</label>
              <input className="po2-in po2-tnum" type="number" value={adHoc.price} onChange={(e) => setAdHoc({ ...adHoc, price: e.target.value })} />
            </div>
          </div>
          <div className="po2-field" style={{ marginTop: 12 }}>
            <label>{t("Description")}</label>
            <input className="po2-in" value={adHoc.description} onChange={(e) => setAdHoc({ ...adHoc, description: e.target.value })} />
          </div>
          <div className="po2-grid po2-g2" style={{ marginTop: 12 }}>
            <div className="po2-field">
              <label>{t("Quantity")}</label>
              <input className="po2-in po2-tnum" type="number" min={1} value={adHoc.quantity} onChange={(e) => setAdHoc({ ...adHoc, quantity: e.target.value })} />
            </div>
            <div className="po2-field">
              <label>{t("Note")}</label>
              <input className="po2-in" value={adHoc.note} onChange={(e) => setAdHoc({ ...adHoc, note: e.target.value })} />
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        title={t("Confirm on Their Behalf")}
        visible={confirmNameModal}
        onCancel={() => setConfirmNameModal(false)}
        onOk={() => {
          if (!confirmedByName) {
            NotificationManager.error(t("A name is required."));
            return;
          }
          setConfirmNameModal(false);
          doTransition("CONFIRM_BEHALF", { confirmedByName });
        }}
      >
        <div className="po2-scope" dir={isRtl ? "rtl" : "ltr"} style={{ padding: 0, background: "transparent", border: "none" }}>
          <div className="po2-insight">
            <b>{t("The system records this permanently")}</b>
            {t("The person's name, your user account, and the timestamp are written to the immutable event log.")}
          </div>
          <div className="po2-field">
            <label>{t("Name of the person who confirmed verbally")}</label>
            <input className="po2-in" value={confirmedByName} onChange={(e) => setConfirmedByName(e.target.value)} />
          </div>
        </div>
      </Modal>

      {/* Close-order confirm modal - restates buyer and exact amount (spec's own named
          hard requirement), not a plain confirm() dialog. */}
      <Modal
        title={closeConfirm ? t(closeConfirm.label) : ""}
        visible={!!closeConfirm}
        onCancel={() => setCloseConfirm(null)}
        onOk={() => {
          const action = closeConfirm;
          setCloseConfirm(null);
          doTransition(action.event);
        }}
        okText={closeConfirm?.terminal ? t("Yes - paid") + ` $${order?.orderTotal?.toFixed(2) ?? "0.00"}` + t(" and collected") : t("Confirm")}
      >
        {closeConfirm && (
          <div className="po2-scope" dir={isRtl ? "rtl" : "ltr"} style={{ padding: 0, background: "transparent", border: "none" }}>
            <div className={`po2-insight ${closeConfirm.terminal ? "po2-warn" : ""}`}>
              <b>{closeConfirm.terminal ? t("This cannot be undone") : t("Please confirm")}</b>
              {t(closeConfirm.note)}
            </div>
            <div style={{ display: "flex", gap: 28 }}>
              <div>
                <div className="po2-field"><label>{t("Buyer")}</label></div>
                <b style={{ fontSize: 15 }}>{orderContact?.label}</b>
              </div>
              <div>
                <div className="po2-field"><label>{t("Amount")}</label></div>
                <b style={{ fontSize: 22, fontVariantNumeric: "tabular-nums" }}>
                  ${order?.orderTotal?.toFixed(2) ?? "0.00"}
                </b>
                <div className="po2-sub">
                  {order?.lines?.length || 0} {t("lines")}
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default VendorOrderBuilder;
